import type {
  BadgeDisplaySymbol,
  BadgePatternId,
  BadgeShapeId,
  BadgeToneId
} from '../../badges'
import {
  BADGE_PATTERN_IDS,
  BADGE_SHAPE_IDS,
  BADGE_TONE_IDS,
  isUntitledIconName
} from '../../badges'
import type { BaseItem } from '../../../stories/table/Table'

export interface MotivationalBadgeConfig {
  colorId: BadgeToneId
  patternId: BadgePatternId | null
  shapeId: BadgeShapeId
  symbol: BadgeDisplaySymbol
}

export interface MotivationalProfileOption {
  email: string | null
  id: string
  imgUrl: string | null
  name: string | null
}

export interface BadgeRow extends BaseItem {
  config: MotivationalBadgeConfig
  createdAt: string
  id: number
  name: string
  targetId: number | null
  targetLabel: string | null
  topNValue: number | null
}

export interface BadgeAssignmentRow extends BaseItem {
  badgeConfig: MotivationalBadgeConfig
  badgeId: number
  badgeName: string
  challengeId: number | null
  challengeResultId: number | null
  challengeTitle: string | null
  goalAchievedAt: string | null
  id: string
  issuedAt: string | null
  issuedBadgeId: string | null
  profileId: string
  profileImgUrl: string | null
  profileName: string | null
  rewardId: number | null
  source: 'challenge' | 'manual' | 'target'
  status: 'dismissed' | 'issued'
  targetId: number | null
}

export type ManualAssignmentState = {
  item: BadgeRow
} | null

const DEFAULT_BADGE_CONFIG: MotivationalBadgeConfig = {
  colorId: 'violet',
  patternId: 'dotGrid',
  shapeId: 'shield',
  symbol: {
    kind: 'icon',
    iconName: 'Calendar'
  }
}

function isBadgeShapeId(value: unknown): value is BadgeShapeId {
  return (
    typeof value === 'string' && BADGE_SHAPE_IDS.includes(value as BadgeShapeId)
  )
}

function isBadgeToneId(value: unknown): value is BadgeToneId {
  return (
    typeof value === 'string' && BADGE_TONE_IDS.includes(value as BadgeToneId)
  )
}

function isBadgePatternId(value: unknown): value is BadgePatternId {
  return (
    typeof value === 'string' &&
    BADGE_PATTERN_IDS.includes(value as BadgePatternId)
  )
}

function normalizeBadgeSymbol(value: unknown): BadgeDisplaySymbol {
  if (typeof value !== 'object' || value === null) {
    return DEFAULT_BADGE_CONFIG.symbol
  }

  const candidate = value as {
    emoji?: unknown
    iconName?: unknown
    kind?: unknown
  }

  if (candidate.kind === 'emoji' && typeof candidate.emoji === 'string') {
    return {
      kind: 'emoji',
      emoji: candidate.emoji
    }
  }

  if (
    candidate.kind === 'icon' &&
    typeof candidate.iconName === 'string' &&
    isUntitledIconName(candidate.iconName)
  ) {
    return {
      kind: 'icon',
      iconName: candidate.iconName
    }
  }

  return DEFAULT_BADGE_CONFIG.symbol
}

export function getDefaultBadgeConfig() {
  return DEFAULT_BADGE_CONFIG
}

export function normalizeBadgeConfig(
  config?: unknown
): MotivationalBadgeConfig {
  if (typeof config !== 'object' || config === null) {
    return DEFAULT_BADGE_CONFIG
  }

  const candidate = config as Partial<MotivationalBadgeConfig>

  return {
    colorId: isBadgeToneId(candidate.colorId)
      ? candidate.colorId
      : DEFAULT_BADGE_CONFIG.colorId,
    patternId:
      candidate.patternId === null || isBadgePatternId(candidate.patternId)
        ? candidate.patternId
        : DEFAULT_BADGE_CONFIG.patternId,
    shapeId: isBadgeShapeId(candidate.shapeId)
      ? candidate.shapeId
      : DEFAULT_BADGE_CONFIG.shapeId,
    symbol: normalizeBadgeSymbol(candidate.symbol)
  }
}
